"use client";
import { about } from "@data";
import { useRef } from "react";
import Link from "next/link";
import { motion, useInView } from "framer-motion";
import { Avatar, Card, Chip } from "@heroui/react";
import { CalendarDays, ExternalLink } from "lucide-react";

const WORD_STAGGER = 0.08;
const WORD_DURATION = 0.4;

const Work = () => {
	const ref = useRef(null);
	const inView = useInView(ref, { once: true });
	return (
		<div ref={ref} className="flex w-full flex-col justify-center gap-3" id="work">
			<h2 className="text-2xl font-bold">
				{"Work Experience".split(" ").map((word, i) => (
					<motion.span key={i} className="mr-1 inline-block" initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: WORD_DURATION, delay: i * WORD_STAGGER, ease: "easeOut" }}>
						{word}
					</motion.span>
				))}
			</h2>
			<Card className="flex w-full flex-col items-center justify-center gap-0 p-0" variant="transparent">
				{about.work.map((work, index) => (
					<Card.Content className="flex w-full flex-col justify-center gap-3 py-5 not-last:border-b" key={index}>
						<div className="xs:flex-row xs:items-center flex flex-col justify-between gap-4">
							<motion.div className="flex items-center justify-center gap-2" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, ease: "easeOut" }}>
								<Avatar>
									<Avatar.Image alt={work.organization} src={work.logo} />
									<Avatar.Fallback>{work.organization.charAt(0)}</Avatar.Fallback>
								</Avatar>
								<motion.div whileHover="hover" className="flex w-full flex-col justify-center">
									<Link className="flex items-center gap-1 hover:gap-2" href={work?.organizationUrl} target="_blank" rel="noreferrer noopener">
										<motion.h3 className="line-clamp-1 font-semibold break-all" variants={{ hover: { scale: 1.05 } }} transition={{ duration: 0.2, ease: "easeOut" }}>
											{work.organization}
										</motion.h3>
										<motion.div variants={{ hover: { x: [0, -3, 3, -3, 3, 0] } }} transition={{ duration: 0.4, ease: "easeInOut" }}>
											<ExternalLink size={16} />
										</motion.div>
									</Link>
									<p className="text-muted-foreground text-xs">{work.role}</p>
								</motion.div>
							</motion.div>
							<motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2, ease: "easeOut" }}>
								<Chip className="whitespace-nowrap" color="default" size="lg" variant="soft">
									<CalendarDays size={16} />
									{work.startDate} - {work.endDate}
								</Chip>
							</motion.div>
						</div>
						<ul className="list-inside list-disc">
							{work.achievements &&
								work.achievements.map((achievement, i) => (
									<motion.li className="indent-3 text-sm" key={i} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.4 + i * 0.1, ease: "easeOut" }}>
										{achievement}
									</motion.li>
								))}
						</ul>
					</Card.Content>
				))}
			</Card>
		</div>
	);
};

export default Work;
